import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, User, Mail, Shield, Check, AlertCircle, Loader, Lock, Hash, Eye, EyeOff, ArrowLeft, Fingerprint, ShieldCheck } from 'lucide-react';

const API_BASE_URL = "https://catbackend.xraidigital.com/api/vitals";
// const API_BASE_URL = "http://127.0.0.1:8000/api/vitals";

/* ─────────────────────────────────────────
   FACE AUTH COMPONENT
───────────────────────────────────────── */
export const FaceAuthScreen = ({ onLoginSuccess, onSwitchToPassword }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [autoScanAttempted, setAutoScanAttempted] = useState(false);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [regPhoto, setRegPhoto] = useState(null);
  const [regPhotoPreview, setRegPhotoPreview] = useState(null);
  const [loginPhoto, setLoginPhoto] = useState(null);
  const [loginPhotoPreview, setLoginPhotoPreview] = useState(null);

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraOpen(false);
  }, []);

  const startCamera = useCallback(async (mode) => {
    setError(null);
    setSuccess(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      streamRef.current = stream;
      setIsCameraOpen(true);
      setCameraMode(mode);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 50);
    } catch {
      setError("Camera access denied.");
    }
  }, []);

  const performLogin = useCallback(async (photoFile) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('photo', photoFile);
    try {
      const response = await fetch(`${API_BASE_URL}/scan-photo/`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.access === "GRANTED") {
        setSuccess("Access Granted! Welcome back!");
        setTimeout(() => onLoginSuccess?.(), 1500);
      } else {
        setError("Face not recognized. Please register first.");
        setTimeout(() => { setActiveTab('register'); setError(null); }, 3000);
      }
    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  }, [onLoginSuccess]);

  const autoCaptureAndLogin = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg');
    setLoginPhotoPreview(dataUrl);
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "face_capture.jpg", { type: "image/jpeg" });
      setLoginPhoto(file);
      stopCamera();
      await performLogin(file);
    }, 'image/jpeg');
  }, [stopCamera, performLogin]);

  useEffect(() => {
    if (!autoScanAttempted) {
      startCamera('login');
      setAutoScanAttempted(true);
    }
  }, [autoScanAttempted, startCamera]);

  useEffect(() => {
    if (isCameraOpen && cameraMode === 'login' && videoRef.current) {
      const timer = setTimeout(() => { autoCaptureAndLogin(); }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isCameraOpen, cameraMode, autoCaptureAndLogin]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg');
    if (cameraMode === 'login') setLoginPhotoPreview(dataUrl);
    else setRegPhotoPreview(dataUrl);
    canvas.toBlob((blob) => {
      const file = new File([blob], "face_capture.jpg", { type: "image/jpeg" });
      if (cameraMode === 'login') setLoginPhoto(file);
      else setRegPhoto(file);
    }, 'image/jpeg');
    stopCamera();
  };

  const handleLogin = async () => {
    if (!loginPhoto) return setError("Capture a photo to login.");
    await performLogin(loginPhoto);
  };

  const handleRegister = async (e) => {
    if (e) e.preventDefault();
    setError(null); setSuccess(null);
    if (!regName || !regEmail) return setError("Please enter name and email.");
    if (!regPhoto) return setError("Please capture a photo first.");
    setIsLoading(true);
    const formData = new FormData();
    formData.append('name', regName);
    formData.append('email', regEmail);
    formData.append('photo', regPhoto);
    try {
      const response = await fetch(`${API_BASE_URL}/upload_detail/`, { method: 'POST', body: formData });
      const data = await response.json();
      if (response.ok) {
        setSuccess("User registered successfully!");
        setRegName(''); setRegEmail(''); setRegPhoto(null); setRegPhotoPreview(null);
        setTimeout(() => { setActiveTab('login'); setSuccess(null); startCamera('login'); }, 2000);
      } else {
        setError(data.error || "Registration failed.");
      }
    } catch {
      setError("Network error. Is the backend running?");
    } finally {
      setIsLoading(false);
    }
  };

  const currentPreview = activeTab === 'login' ? loginPhotoPreview : regPhotoPreview;

  return (
    <div className="auth-wrapper">
      <style>{AUTH_CSS}</style>
      <div className="auth-card">
        <div className="card-header face-header">
          <div className="header-icon-wrap face-icon-wrap">
            <Fingerprint size={36} className="header-icon" />
          </div>
          <h2 className="header-title">U4Rad Biometric Secure</h2>
          <p className="header-sub">
            {activeTab === 'login' ? 'Auto Face Recognition' : 'Register New User'}
          </p>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => { setActiveTab('login'); setError(null); setSuccess(null); }}>
            Face Login
          </button>
          <button className={`tab-btn ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => { setActiveTab('register'); setError(null); setSuccess(null); }}>
            Register
          </button>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-error"><AlertCircle size={15} />{error}</div>}
          {success && <div className="alert alert-success"><Check size={15} />{success}</div>}
          {isLoading && <div className="alert alert-info"><Loader size={15} className="spin" />Verifying face...</div>}

          <div className="viewer">
            {isCameraOpen ? (
              <div className="cam-wrap">
                <video ref={videoRef} autoPlay playsInline className="cam-video" />
                <div className="cam-overlay">
                  <div className="viewfinder" />
                  {activeTab === 'login' && <div className="scan-label">Scanning face…</div>}
                </div>
                {activeTab === 'register' && (
                  <button className="capture-btn" onClick={capturePhoto} />
                )}
              </div>
            ) : (
              <div className="preview-wrap">
                {currentPreview
                  ? <img src={currentPreview} className="preview-img" alt="Captured face" />
                  : <div className="preview-empty"><Camera size={44} /><span>Ready for Capture</span></div>}
              </div>
            )}
          </div>

          <div className="form-area">
            {activeTab === 'register' && (
              <>
                <div className="input-row">
                  <User size={15} className="input-icon" />
                  <input className="input-field" placeholder="Full Name" value={regName} onChange={e => setRegName(e.target.value)} />
                </div>
                <div className="input-row">
                  <Mail size={15} className="input-icon" />
                  <input className="input-field" placeholder="Email" type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} />
                </div>
                {!isCameraOpen && (
                  <button className="btn-secondary" onClick={() => startCamera('register')}>
                    <Camera size={16} />{currentPreview ? 'Retake Photo' : 'Capture Face Photo'}
                  </button>
                )}
                <button className="btn-primary" disabled={isLoading || isCameraOpen} onClick={handleRegister}>
                  {isLoading ? <Loader className="spin" size={18} /> : 'Complete Registration'}
                </button>
              </>
            )}

            {activeTab === 'login' && !isCameraOpen && !isLoading && (
              <>
                {loginPhotoPreview && (
                  <button className="btn-primary" onClick={handleLogin} disabled={isLoading}>
                    Verify &amp; Login
                  </button>
                )}
                <button className="btn-secondary" onClick={() => startCamera('login')}>
                  <Camera size={16} />Scan Face Again
                </button>
              </>
            )}
          </div>

          <div className="divider"><span>or</span></div>
          <button className="switch-mode-btn" onClick={onSwitchToPassword}>
            <Lock size={14} />Switch to Password Login
          </button>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

/* ─────────────────────────────────────────
   PASSWORD LOGIN COMPONENT
───────────────────────────────────────── */
export const PasswordLoginScreen = ({ onLoginSuccess, onGoToRegister }) => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!userId.trim() || !password.trim()) return setError("Please enter User ID and Password.");
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/login-user/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId.trim(), password: password.trim() }),
      });
      const data = await response.json();
      if (data.access === "GRANTED") {
        localStorage.setItem("user_id", userId.trim());
        localStorage.setItem("user_data", JSON.stringify(data.user));
        onLoginSuccess?.(data.user);
      } else {
        setError(data.error || "Invalid credentials.");
      }
    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <style>{AUTH_CSS}</style>
      <div className="auth-card">
        <div className="card-header pw-header">
          <img src="/logo192.png" alt="U4Rad Logo" style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 8 }} />
          <div className="header-icon-wrap pw-icon-wrap">
            <Shield size={36} className="header-icon" />
          </div>
          <h2 className="header-title">U4Rad Secure Login</h2>
          <p className="header-sub">Sign in with your credentials</p>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-error"><AlertCircle size={15} />{error}</div>}

          <form onSubmit={handleSubmit} className="form-area">
            <div className="input-row">
              <Hash size={15} className="input-icon" />
              <input
                className="input-field"
                placeholder="User ID"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="input-row">
              <Lock size={15} className="input-icon" />
              <input
                className="input-field"
                placeholder="Password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPw(v => !v)}>
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>

            <button className="btn-primary" type="submit" disabled={isLoading}>
              {isLoading ? <Loader className="spin" size={18} /> : 'Sign In'}
            </button>
          </form>

          <p className="register-prompt">
            Don't have an account?{' '}
            <button className="link-btn" onClick={onGoToRegister}>Create one</button>
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   REGISTER COMPONENT
───────────────────────────────────────── */
export const PasswordRegisterScreen = ({ onRegisterSuccess, onGoToLogin }) => {
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState(false);

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setIsCameraOpen(false);
  };

  useEffect(() => { return () => stopCamera(); }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      setIsCameraOpen(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 50);
    } catch { setError("Camera access denied."); }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    setPhotoPreview(canvas.toDataURL('image/jpeg'));
    canvas.toBlob((blob) => {
      setPhoto(new File([blob], "face.jpg", { type: "image/jpeg" }));
    }, 'image/jpeg');
    stopCamera();
  };

  const validateForm = () => {
    setError(null); setSuccess(null);
    if (!userId.trim() || !name.trim() || !email.trim() || !password.trim()) {
      setError("All fields are required."); return false;
    }
    if (password !== confirmPw) { setError("Passwords do not match."); return false; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return false; }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConsent(true);
    setConsentAgreed(false);
  };

  const doRegister = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('user_id', userId.trim());
    formData.append('name', name.trim());
    formData.append('email', email.trim());
    formData.append('password', password.trim());
    if (photo) formData.append('photo', photo);

    try {
      const response = await fetch(`${API_BASE_URL}/register-user/`, { method: 'POST', body: formData });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess("Account created! Redirecting to login…");
        setTimeout(() => onRegisterSuccess?.(), 1800);
      } else {
        setError(data.error || "Registration failed.");
      }
    } catch {
      setError("Network error. Is the backend running?");
    } finally {
      setIsLoading(false);
    }
  };

  if (showConsent) {
    return (
      <div className="auth-wrapper">
        <style>{AUTH_CSS}</style>
        <div className="auth-card" style={{ maxWidth: 480 }}>
          <div className="card-header pw-header">
          <img src="/logo192.png" alt="U4Rad Logo" style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 8 }} />
            <div className="header-icon-wrap pw-icon-wrap">
              <ShieldCheck size={36} className="header-icon" />
            </div>
            <h2 className="header-title">Patient Consent</h2>
            <p className="header-sub">Please read and agree before creating your account</p>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.65, marginBottom: 12 }}>
              By proceeding, I acknowledge that I voluntarily consent to the collection and processing of my facial video solely for estimating physiological parameters.
            </p>
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#D97706', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={14} /> I understand that:
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'This application is intended for informational and wellness purposes only.',
                  'The results are AI-generated estimates and may not be clinically accurate.',
                  'This application is NOT a medical device.',
                  'The results must not be used for medical diagnosis, treatment, emergency decisions, or as a substitute for professional healthcare advice.',
                  'If I have any medical concerns, abnormal symptoms, or an emergency, I will consult a qualified healthcare professional immediately.',
                ].map((text, i) => (
                  <li key={i} style={{ padding: '3px 0 3px 14px', fontSize: 13, color: '#475569', lineHeight: 1.5, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, top: 8, width: 5, height: 5, borderRadius: '50%', background: '#14B8A6' }} />
                    {text}
                  </li>
                ))}
              </ul>
            </div>
            <label
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: 10, border: `2px solid ${consentAgreed ? '#0F766E' : '#E2E8F0'}`, borderRadius: 10, marginBottom: 16, background: consentAgreed ? 'rgba(15,118,110,0.03)' : '#fff', transition: 'all 0.2s' }}
            >
              <div
                onClick={() => setConsentAgreed(!consentAgreed)}
                style={{ width: 22, height: 22, minWidth: 22, borderRadius: 6, border: `2px solid ${consentAgreed ? '#0F766E' : '#CBD5E1'}`, background: consentAgreed ? '#0F766E' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginTop: 1, transition: 'all 0.2s', cursor: 'pointer' }}
              >
                {consentAgreed && <Check size={14} />}
              </div>
              <span style={{ fontSize: 14, color: '#475569', fontWeight: 500, lineHeight: 1.5 }}>
                I have read and agree to the above consent and disclaimer.
              </span>
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn-secondary" onClick={() => setShowConsent(false)} style={{ flex: 1 }}>
                Back
              </button>
              <button
                className="btn-primary"
                disabled={!consentAgreed || isLoading}
                onClick={doRegister}
                style={{ flex: 2, ...(!consentAgreed ? { opacity: 0.4, cursor: 'not-allowed', background: '#CBD5E1', boxShadow: 'none' } : {}) }}
              >
                {isLoading ? <Loader className="spin" size={18} /> : 'Agree & Create Account'}
              </button>
            </div>
            <p style={{ textAlign: 'center', fontSize: 12, color: '#94A3B8', marginTop: 12 }}>
              Powered by <strong style={{ color: '#0F766E' }}>U4Rad AI</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrapper">
      <style>{AUTH_CSS}</style>
      <div className="auth-card wide-card">
        <div className="card-header reg-header">
          <img src="/logo192.png" alt="U4Rad Logo" style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 8 }} />
          <div className="header-icon-wrap reg-icon-wrap">
            <User size={36} className="header-icon" />
          </div>
          <h2 className="header-title">Create Account</h2>
          <p className="header-sub">Set up your U4Rad secure access</p>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-error"><AlertCircle size={15} />{error}</div>}
          {success && <div className="alert alert-success"><Check size={15} />{success}</div>}

          <form onSubmit={handleSubmit} className="form-area">
            <div className="two-col">
              <div className="input-row">
                <Hash size={15} className="input-icon" />
                <input className="input-field" placeholder="User ID (unique)" value={userId}
                  onChange={e => setUserId(e.target.value)} autoComplete="username" />
              </div>
              <div className="input-row">
                <User size={15} className="input-icon" />
                <input className="input-field" placeholder="Full Name" value={name}
                  onChange={e => setName(e.target.value)} />
              </div>
            </div>

            <div className="input-row">
              <Mail size={15} className="input-icon" />
              <input className="input-field" placeholder="Email Address" type="email" value={email}
                onChange={e => setEmail(e.target.value)} autoComplete="email" />
            </div>

            <div className="two-col">
              <div className="input-row">
                <Lock size={15} className="input-icon" />
                <input className="input-field" placeholder="Password" type={showPw ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
                <button type="button" className="pw-toggle" onClick={() => setShowPw(v => !v)}>
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div className="input-row">
                <Lock size={15} className="input-icon" />
                <input className="input-field" placeholder="Confirm Password" type="password"
                  value={confirmPw} onChange={e => setConfirmPw(e.target.value)} autoComplete="new-password" />
              </div>
            </div>

            <div className="face-optional">
              <p className="optional-label"><Camera size={13} /> Optional: Add face for biometric login</p>
              {isCameraOpen ? (
                <div className="mini-cam">
                  <video ref={videoRef} autoPlay playsInline className="mini-video" />
                  <button type="button" className="mini-capture" onClick={capturePhoto}>Capture</button>
                </div>
              ) : (
                <div className="mini-preview-row">
                  {photoPreview
                    ? <img src={photoPreview} className="mini-preview" alt="face" />
                    : <div className="mini-empty"><Camera size={22} /></div>}
                  <button type="button" className="btn-secondary sm" onClick={startCamera}>
                    {photoPreview ? 'Retake' : 'Add Face Photo'}
                  </button>
                </div>
              )}
            </div>

            <button className="btn-primary" type="submit">
              Create Account
            </button>
          </form>

          <p className="register-prompt">
            Already have an account?{' '}
            <button className="link-btn" onClick={onGoToLogin}><ArrowLeft size={12} />Back to Login</button>
          </p>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

/* ─────────────────────────────────────────
   DEFAULT EXPORT — drop-in for App.js
   Usage in App.js:
     import FaceAuth from './component/FaceAuth';
     <FaceAuth onLoginSuccess={() => setCurrentPage('home')} />
───────────────────────────────────────── */
const FaceAuth = ({ onLoginSuccess }) => {
  const [screen, setScreen] = useState('password');

  const handleLoginSuccess = (user) => {
    onLoginSuccess?.(user);
  };

  return (
    <>
      {screen === 'password' && (
        <PasswordLoginScreen
          onLoginSuccess={handleLoginSuccess}
          onGoToRegister={() => setScreen('register')}
        />
      )}
      {screen === 'register' && (
        <PasswordRegisterScreen
          onRegisterSuccess={() => setScreen('password')}
          onGoToLogin={() => setScreen('password')}
        />
      )}
    </>
  );
};

export default FaceAuth;

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const AUTH_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-wrapper {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #F8FAFC;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }

  .auth-card {
    width: 100%;
    max-width: 420px;
    background: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.12);
  }
  .wide-card { max-width: 520px; }

  .card-header {
    padding: 32px 28px 24px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .face-header { background: linear-gradient(135deg, #F0FDF4 0%, #CCFBF1 100%); }
  .pw-header   { background: linear-gradient(135deg, #F0FDF4 0%, #CCFBF1 100%); }
  .reg-header  { background: linear-gradient(135deg, #F0FDF4 0%, #CCFBF1 100%); }

  .card-header::before {
    content: '';
    position: absolute; inset: 0;
    background: repeating-linear-gradient(
      45deg, transparent, transparent 24px,
      rgba(15,118,110,0.02) 24px, rgba(15,118,110,0.02) 25px
    );
  }

  .header-icon-wrap {
    width: 68px; height: 68px; border-radius: 18px;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 14px;
    border: 1px solid rgba(15,118,110,0.15);
    position: relative;
  }
  .face-icon-wrap { background: rgba(15,118,110,0.1); border-color: rgba(15,118,110,0.25); }
  .pw-icon-wrap   { background: rgba(15,118,110,0.1);  border-color: rgba(15,118,110,0.25); }
  .reg-icon-wrap  { background: rgba(15,118,110,0.1);  border-color: rgba(15,118,110,0.25); }

  .face-header .header-icon { color: #0F766E; }
  .pw-header   .header-icon { color: #0F766E; }
  .reg-header  .header-icon { color: #0F766E; }

  .header-title {
    color: #0F172A; font-size: 22px; font-weight: 700;
    letter-spacing: -0.3px; position: relative;
  }
  .header-sub {
    color: #64748B; font-size: 13px; margin-top: 5px; position: relative;
  }

  .tab-bar {
    display: flex; border-bottom: 1px solid #E2E8F0; background: #fff;
  }
  .tab-btn {
    flex: 1; padding: 14px; border: none; background: none; cursor: pointer;
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
    color: #94A3B8; border-bottom: 2px solid transparent; transition: all 0.2s;
  }
  .tab-btn.active { color: #0F766E; border-bottom-color: #0F766E; }
  .tab-btn:hover:not(.active) { color: #475569; }

  .card-body { padding: 24px; }

  .alert {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: 10px;
    font-size: 13px; margin-bottom: 18px;
  }
  .alert-error   { background: #FEF2F2;  color: #DC2626; border: 1px solid #FECACA; }
  .alert-success { background: #F0FDF4;  color: #059669; border: 1px solid #BBF7D0; }
  .alert-info    { background: #F0FDF4; color: #0F766E; border: 1px solid #99F6E4; }

  .viewer {
    height: 270px; border-radius: 14px; overflow: hidden;
    position: relative; margin-bottom: 20px;
    background: #000; border: 1px solid #E2E8F0;
  }
  .cam-wrap, .preview-wrap { width: 100%; height: 100%; position: relative; }
  .cam-video { width: 100%; height: 100%; object-fit: cover; transform: scaleX(-1); }
  .cam-overlay {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center; flex-direction: column;
  }
  .viewfinder {
    width: 160px; height: 160px; border-radius: 50%;
    border: 2px dashed rgba(20,184,166,0.8);
    box-shadow: 0 0 0 1000px rgba(0,0,0,0.5);
    animation: pulse-ring-auth 2s infinite;
  }
  @keyframes pulse-ring-auth {
    0%, 100% { border-color: rgba(20,184,166,0.8); }
    50% { border-color: rgba(20,184,166,0.3); }
  }
  .scan-label {
    color: #14B8A6; margin-top: 18px; font-size: 13px; font-weight: 600;
    background: rgba(15,118,110,0.2); padding: 6px 16px; border-radius: 20px;
    border: 1px solid rgba(15,118,110,0.3);
  }
  .capture-btn {
    position: absolute; bottom: 18px; left: 50%; transform: translateX(-50%);
    width: 52px; height: 52px; border-radius: 50%;
    border: 3px solid white; background: rgba(255,255,255,0.18); cursor: pointer;
    transition: background 0.2s;
  }
  .capture-btn:hover { background: rgba(255,255,255,0.3); }
  .preview-wrap { display: flex; align-items: center; justify-content: center; }
  .preview-img { width: 100%; height: 100%; object-fit: cover; }
  .preview-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #94A3B8; }

  .form-area { display: flex; flex-direction: column; gap: 13px; }

  .input-row {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px; background: #F8FAFC;
    border: 1.5px solid #E2E8F0; border-radius: 11px; transition: border-color 0.2s;
  }
  .input-row:focus-within { border-color: #0F766E; background: #fff; }
  .input-icon { color: #94A3B8; flex-shrink: 0; }
  .input-field {
    border: none; outline: none; background: transparent;
    width: 100%; font-size: 14px; color: #0F172A; font-family: 'Inter', sans-serif;
  }
  .input-field::placeholder { color: #CBD5E1; }

  .pw-toggle {
    background: none; border: none; cursor: pointer; color: #94A3B8;
    padding: 0; display: flex; align-items: center; flex-shrink: 0;
  }
  .pw-toggle:hover { color: #475569; }

  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }

  .btn-primary {
    padding: 13px 20px;
    background: linear-gradient(135deg, #0F766E, #0D9488);
    color: white; border: none; border-radius: 11px;
    font-weight: 700; font-size: 14px; font-family: 'Inter', sans-serif;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: opacity 0.2s, transform 0.15s;
    box-shadow: 0 4px 14px rgba(15,118,110,0.3);
  }
  .btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-secondary {
    padding: 11px 16px; background: #F8FAFC; color: #0F766E;
    border: 1.5px solid #E2E8F0; border-radius: 11px;
    font-weight: 600; font-size: 13px; font-family: 'Inter', sans-serif;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: border-color 0.2s, background 0.2s;
  }
  .btn-secondary:hover { border-color: #0F766E; background: #F0FDF4; }
  .btn-secondary.sm { padding: 8px 14px; font-size: 12px; }

  .divider {
    display: flex; align-items: center; gap: 12px;
    margin: 18px 0 14px; font-size: 12px;
  }
  .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: #E2E8F0; }
  .divider span { color: #94A3B8; white-space: nowrap; }

  .switch-mode-btn {
    width: 100%; padding: 11px; background: transparent;
    border: 1.5px dashed #E2E8F0; border-radius: 10px; color: #64748B;
    font-size: 13px; font-weight: 500; font-family: 'Inter', sans-serif;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: all 0.2s;
  }
  .switch-mode-btn:hover { border-color: #0F766E; color: #0F766E; }

  .register-prompt {
    text-align: center; font-size: 13px; color: #64748B; margin-top: 16px;
    display: flex; align-items: center; justify-content: center; gap: 5px; flex-wrap: wrap;
  }
  .link-btn {
    background: none; border: none; cursor: pointer; color: #0F766E;
    font-weight: 600; font-size: 13px; font-family: 'Inter', sans-serif;
    display: inline-flex; align-items: center; gap: 4px;
    text-decoration: underline; text-underline-offset: 3px;
  }
  .link-btn:hover { color: #0D9488; }

  .face-optional {
    background: #F8FAFC; border: 1.5px solid #E2E8F0; border-radius: 11px; padding: 14px;
  }
  .optional-label {
    color: #64748B; font-size: 12px; font-weight: 600;
    display: flex; align-items: center; gap: 6px; margin-bottom: 10px;
  }
  .mini-cam { position: relative; border-radius: 8px; overflow: hidden; }
  .mini-video { width: 100%; height: 140px; object-fit: cover; transform: scaleX(-1); display: block; }
  .mini-capture {
    position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%);
    background: #0F766E; color: white; border: none; border-radius: 8px;
    padding: 6px 18px; font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .mini-preview-row { display: flex; align-items: center; gap: 12px; }
  .mini-preview { width: 60px; height: 60px; object-fit: cover; border-radius: 8px; border: 1.5px solid #E2E8F0; }
  .mini-empty {
    width: 60px; height: 60px; border-radius: 8px;
    background: #F8FAFC; border: 1.5px solid #E2E8F0;
    display: flex; align-items: center; justify-content: center; color: #CBD5E1;
  }

  .spin { animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 480px) {
    .two-col { grid-template-columns: 1fr; }
    .auth-card, .wide-card { max-width: 100%; }
  }
`;